import PaymentMethod from '@alltrails/amplitude/enums/PaymentMethod';
import logPurchaseSuccess from '@alltrails/amplitude/events/logPurchaseSuccess';
import type CurrencyCode from '@alltrails/core/types/CurrencyCode';
import type { Plan } from '@alltrails/types/Plan';
import { trackTinuitiPurchase } from '@alltrails/shared/utils/tinuitiUtils';
import logError from '@alltrails/core/utils/logError';
import type Coupon from '@alltrails/types/Coupon';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import { trackTinuitiOOHPurchase } from './TinuitiOOHPixel';
import { getPriceTotal } from './plan';
import getPurchaseScreenTypePlan from './getPurchaseScreenTypePlan';

type Params = {
  userId: number;
  currencyCode: CurrencyCode;
  plan: Plan;
  paymentMethod: PaymentMethod;
  invoiceUuid?: string;
  coupon?: Coupon;
} & ({ webProPurchaseLocation: 'modal'; trigger: UpsellTrigger } | { webProPurchaseLocation: 'pro page'; trigger?: never });

function handleAttribution({ userId, currencyCode, plan, webProPurchaseLocation, paymentMethod, invoiceUuid, coupon, trigger }: Params) {
  // Only use passed-in userId if there isn't one stored on the current session in Amplitude.
  // Users can purchase on an associated userId without being authenticated through the use of the uid query
  // parameter used on promo emails, and therefore we want to associate the purchase with the correct user id
  // but without opening up our analytics to other weirdness outside of a purchase.
  let existingUserId;

  if (window.amplitude) {
    existingUserId = window.amplitude.getUserId();
  }

  const { recurlyPlanCode, priceTotal } = plan;
  const value = getPriceTotal(plan, coupon);

  // gtag events (not defined in local development)
  if (typeof gtag !== 'undefined') {
    if (!existingUserId) {
      // Set GA user id
      // https://developers.google.com/analytics/devguides/collection/gtagjs/cookies-user-id
      gtag('config', window.ga4_measure_id, {
        user_id: userId
      });
    }
    // Currently, the only purchase option for GA UA config
    gtag('event', 'page_view', { page_path: '/pro/year/success' });
    // GA4
    // https://developers.google.com/analytics/devguides/collection/ga4/reference/events#purchase
    gtag('event', 'purchase', {
      currency: currencyCode,
      value,
      items: [
        {
          item_id: recurlyPlanCode,
          coupon: coupon?.code,
          discount: Number(plan.priceTotal) - value,
          item_category: 'Pro Subscription',
          currency: currencyCode,
          price: priceTotal
        }
      ]
    });
  }

  // FB Pixel
  // https://developers.facebook.com/docs/meta-pixel/reference#:~:text=PAGE_VIEW-,Purchase,-When%20a%20purchase
  // Check for presence of fbq in case of content blockers
  if (typeof fbq !== 'undefined') {
    if (!existingUserId) {
      fbq('init', process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID, {
        external_id: userId
      });
    }
    fbq('track', 'Purchase', {
      currency: currencyCode,
      value,
      content_type: 'product',
      content_ids: [recurlyPlanCode]
    });
  }

  // TikTok Pixel
  // https://ads.tiktok.com/help/article/get-started-pixel?lang=en
  if (typeof ttq !== 'undefined') {
    if (userId) {
      ttq.identify({
        external_id: userId
      });
    }
    ttq.track('CompletePayment', {
      content_type: 'product',
      description: 'Pro Subscription',
      quantity: 1,
      content_id: recurlyPlanCode,
      currency: currencyCode,
      value
    });
  }

  // Tinuiti Pixel
  if (existingUserId || userId) {
    const tinuitiUserId = existingUserId || userId;
    trackTinuitiPurchase(value.toString(), tinuitiUserId.toString());
  }

  // Tinuiti OOH Pixel
  trackTinuitiOOHPurchase(value, currencyCode);

  // Amplitude
  if (!existingUserId) {
    if (window.amplitude) {
      window.amplitude.setUserId(userId.toString());
    }
  }

  logPurchaseSuccess({
    campaign_id: coupon?.code as string, // analytics needs to update purchase success to make this optional
    payment_method: paymentMethod,
    pro_upsell_experiment_variant_name: '', // only required on mobile
    product_id: recurlyPlanCode,
    purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon),
    purchase_screen_type: undefined, // only required on mobile
    transaction_date: '', // only required on mobile
    transaction_id: '', // only required on mobile
    upsell_trigger: trigger,
    web_purchase_location: webProPurchaseLocation
  });

  // MNTN Conversion Pixel
  try {
    if (process.env.NEXT_PUBLIC_MNTN_PIXEL_ID && window && window.localStorage) {
      const orderId = invoiceUuid ? `web_${invoiceUuid}` : '';
      window.localStorage.setItem('mntn-conversion-data', JSON.stringify({ orderId, totalOrderAmount: value }));
    }
  } catch (e) {
    logError(new Error(`Error setting MNTN conversion data: ${e}`));
  }
}

export default function handlePaymentFormSuccess(params: Params) {
  // ensure that attribution does not cause any errors
  // that would prevent a user that successfully purchased plus
  // from continuing with the expected flow
  try {
    handleAttribution(params);
  } catch (e) {
    logError(new Error(`Error handling payment attribution: ${e}`));
  }
}
